/**
 * TopbarSearchContext — shared search input state for the global
 * topbar search field. Pages that want to participate (e.g. Leads,
 * Projects) read `query` from this context to filter their data.
 *
 * Reset semantics: the query clears automatically on route change so
 * leftover input from a previous page doesn't filter the next one.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

type Ctx = {
  query: string;
  setQuery: (v: string) => void;
  /** Lets a page set a route-specific placeholder dynamically. */
  placeholder: string;
  setPlaceholder: (p: string) => void;
  /** Auto-focus signal — pages bump this to focus the input (used by
   *  ⌘K keyboard shortcut in the topbar). */
  focusToken: number;
  focus: () => void;
};

const Cx = createContext<Ctx>({
  query: '',
  setQuery: () => {},
  placeholder: 'Search…',
  setPlaceholder: () => {},
  focusToken: 0,
  focus: () => {},
});

export function TopbarSearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  const [placeholder, setPlaceholder] = useState('Search…');
  const [focusToken, setFocusToken] = useState(0);
  const { pathname } = useLocation();

  // Clear the query when the route changes — search context is
  // per-page, not persistent across navigation.
  useEffect(() => {
    setQuery('');
  }, [pathname]);

  const focus = useCallback(() => setFocusToken((n) => n + 1), []);

  const value = useMemo(
    () => ({ query, setQuery, placeholder, setPlaceholder, focusToken, focus }),
    [query, placeholder, focusToken, focus],
  );

  return <Cx.Provider value={value}>{children}</Cx.Provider>;
}

export function useTopbarSearch() {
  return useContext(Cx);
}
